import { addKeyGuideline, deleteKeyGuideline, getKeyGuidelines, updateKeyGuidelineOrder } from "@/services/keyguidelines";
import { DotsVertical, Edit03, Trash01 } from "@untitledui/icons";
import React, { useEffect, useState } from "react";



const KeyGuidelinesConfig: React.FC = () => {
  const [guidelines, setGuidelines] = useState<any[]>([]);
  const [initial, setInitial] = useState("");

  const [showForm, setShowForm] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);

  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");

  const [dragIndex, setDragIndex] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const res = await getKeyGuidelines();

    const ordered = [...res].sort(
      (a, b) => a.DisplayOrder - b.DisplayOrder
    );

    setGuidelines(ordered);
    setInitial(JSON.stringify(ordered));
  };

  const resetForm = () => {
    setTitle("");
    setUrl("");
    setEditingId(null);
    setShowForm(false);
  };

  const handleAddClick = () => {
    setEditingId(null);
    setTitle("");
    setUrl("");
    setShowForm(true);
  };

  const handleEdit = (item: any) => {
    setEditingId(item.Id);
    setTitle(item.Title);
    setUrl(item.Url);
    setShowForm(true);
  };

  const handleSubmit = async () => {
  if (!title.trim() || !url.trim()) return;

  if (title.length > 50) {
    alert("Title cannot exceed 50 characters.");
    return;
  }

  if (url.length > 500) {
    alert("URL cannot exceed 500 characters.");
    return;
  }
    if (editingId) {
      await updateKeyGuideline({
        id: editingId,
        title: title.trim(),
        url: url.trim(),
      });
    } else {
      await addKeyGuideline(
        title.trim(),
        url.trim()
      );
    }

    await loadData();
    resetForm();
  };

  const handleDelete = async (id: number) => {
    await deleteKeyGuideline(id);

    const updated = guidelines.filter(
      (g) => g.Id !== id
    );

    const reordered = updated.map(
      (item, index) => ({
        ...item,
        DisplayOrder: index + 1,
      })
    );

    setGuidelines(reordered);
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDrop = (index: number) => {
    if (dragIndex === null) return;

    const updated = [...guidelines];

    const dragged = updated.splice(
      dragIndex,
      1
    )[0];

    updated.splice(index, 0, dragged);

    const reordered = updated.map(
      (item, idx) => ({
        ...item,
        DisplayOrder: idx + 1,
      })
    );

    setGuidelines(reordered);
    setDragIndex(null);
  };

  const handleSaveOrder = async () => {
    await updateKeyGuidelineOrder(
      guidelines.map((item, index) => ({
        id: item.Id,
        displayOrder: index + 1,
      }))
    );

    setInitial(JSON.stringify(guidelines));
  };

  const handleCancelChanges = () => {
    setGuidelines(JSON.parse(initial));
  };

  const hasChanges =
    initial !== "" &&
    JSON.stringify(guidelines) !== initial;

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">

      {/* Header */}

     <div className="flex items-start justify-between">
  <div>
    <div className="text-xs text-gray-500 font-medium">
      Manage Key Guidelines
    </div>

    <div className="text-[11px] text-gray-400">
      Add, edit, remove or reorder guideline links
    </div>
  </div>

  <div className="relative group">
    <button
      onClick={handleAddClick}
      disabled={guidelines.length >= 5}
      className="
        bg-[var(--color-brand)]
        text-white
        px-3
        py-1.5
        rounded
        text-xs
        disabled:opacity-50
        disabled:cursor-not-allowed
      "
    >
      Add New
    </button>

    {guidelines.length >= 5 && (
      <div
        className="
          absolute
          right-0
          top-full
          mt-2
          hidden
          group-hover:block
          whitespace-nowrap
          rounded-md
          bg-red-600
          px-2
          py-1
          text-xs
          text-white
          shadow-lg
          z-10
        "
      >
        Maximum of 5 key guidelines allowed
      </div>
    )}
  </div>
</div>

      {/* Add / Edit Form */}

      {showForm && (
        <div className="border border-gray-200 rounded-md bg-white p-3 space-y-2">
          <input
  type="text"
  placeholder="Title"
  value={title}
  maxLength={50}
  onChange={(e) => setTitle(e.target.value)}
  className="
    w-full
    border
    border-gray-300
    px-3
    py-2
    rounded-md
    text-sm
    focus:outline-none
    focus:ring-2
    focus:ring-orange-400
  "
/>

<div className="text-[11px] text-gray-400 text-right">
  {title.length}/50
</div>

          <input
  type="text"
  placeholder="URL"
  value={url}
  maxLength={500}
  onChange={(e) => setUrl(e.target.value)}
  className="
    w-full
    border
    border-gray-300
    px-3
    py-2
    rounded-md
    text-sm
    focus:outline-none
    focus:ring-2
    focus:ring-orange-400
  "
/>

<div className="text-[11px] text-gray-400 text-right">
  {url.length}/500
</div>

          <div className="flex justify-end gap-2">
            <button
              onClick={resetForm}
              className="
                px-3
                py-1
                text-xs
                rounded
                bg-gray-200
              "
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              className="
                bg-[var(--color-brand)]
                text-white
                px-3
                py-1
                rounded
                text-xs
              "
            >
              {editingId
                ? "Update"
                : "Add"}
            </button>
          </div>
        </div>
      )}

      {/* Guidelines */}

      <div className="space-y-2">
        {guidelines.map((item, index) => (
          <div
            key={item.Id}
            draggable
            onDragStart={() =>
              handleDragStart(index)
            }
            onDragOver={(e) =>
              e.preventDefault()
            }
            onDrop={() =>
              handleDrop(index)
            }
            className="
              px-3
              py-2
              border
              border-gray-200
              rounded-md
              bg-white
              hover:bg-[#f7f5f1]
              cursor-move
            "
          >
            <div className="flex justify-between items-start gap-3">

              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-500 truncate">
                  {item.Title}
                </div>

                <div className="text-[11px] text-gray-500 truncate">
                  {item.Url}
                </div>
              </div>

              <div className="flex items-center gap-3">

  <DotsVertical className="w-4 h-4 text-gray-400 cursor-move" />

  <button
    onClick={() => handleEdit(item)}
    className="
      text-gray-500
      hover:text-[var(--color-brand)]
      transition-colors
    "
    title="Edit"
  >
    <Edit03 className="w-4 h-4" />
  </button>

  <button
    onClick={() => handleDelete(item.Id)}
    className="
      text-gray-500
      hover:text-red-600
      transition-colors
    "
    title="Delete"
  >
    <Trash01 className="w-4 h-4" />
  </button>

</div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}

      <div className="flex justify-end items-center gap-2">

        {hasChanges && (
          <div
            className="
              text-orange-600
              border
              border-orange-300
              bg-orange-50
              px-2
              py-0.5
              rounded
              text-xs
            "
          >
            Unsaved
          </div>
        )}

        <button
          className="
            bg-[#e7e5e0]
            px-3
            py-1
            rounded
            text-xs
          "
          onClick={handleCancelChanges}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="
            bg-[var(--color-brand)]
            text-white
            px-3
            py-1
            rounded
            text-xs
            disabled:opacity-50
          "
          onClick={handleSaveOrder}
          disabled={!hasChanges}
        >
          Save Changes
        </button>

      </div>
    </div>
  );
};

export default KeyGuidelinesConfig;

function updateKeyGuideline(arg0: { id: number; title: string; url: string; }) {
    throw new Error("Function not implemented.");
}
